/**
 * Компонент календаря рабочего графика
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { WorkerApi, type MonthScheduleResponse, type WorkDayInfo } from '../services/apiClient'
import { useWorkerStore } from '../store/workerStore'
import './ScheduleCalendar.css'

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
]

const DAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

export function ScheduleCalendar() {
  const navigate = useNavigate()
  const { employeeId } = useWorkerStore()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [schedule, setSchedule] = useState<MonthScheduleResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadSchedule = useCallback(async () => {
    if (!employeeId) return
    
    setIsLoading(true)
    setError(null)
    
    try {
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const response = await WorkerApi.getEmployeeSchedule(employeeId, year, month)
      setSchedule(response)
    } catch (err) {
      console.error('Failed to load schedule:', err)
      
      // Проверяем, является ли это ошибкой удаленного сотрудника
      const { EmployeeDeletedError } = await import('../errors/EmployeeDeletedError')
      if (err instanceof EmployeeDeletedError) {
        navigate('/employee-deleted')
        return
      }
      
      setError(err instanceof Error ? err.message : 'Ошибка загрузки')
    } finally {
      setIsLoading(false)
    }
  }, [employeeId, currentDate, navigate])

  useEffect(() => {
    loadSchedule()
  }, [loadSchedule])

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
  }

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))
  }

  const getDaysGrid = (): (WorkDayInfo | null)[] => {
    if (!schedule) return []
    
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const startDayOfWeek = (firstDay.getDay() + 6) % 7 // Пн=0, Вс=6
    
    const grid: (WorkDayInfo | null)[] = []
    
    // Пустые ячейки до первого дня
    for (let i = 0; i < startDayOfWeek; i++) {
      grid.push(null)
    }
    
    // Дни месяца
    for (const day of schedule.days) {
      grid.push(day)
    }
    
    return grid
  }

  const isToday = (dateStr: string): boolean => {
    const today = new Date()
    const date = new Date(dateStr)
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear()
  }

  const daysGrid = getDaysGrid()

  return (
    <div className="schedule-container">
      <div className="schedule-card">
        {/* Время работы */}
        {schedule && schedule.work_start && (
          <div className="schedule-work-hours">
            <div className="work-hours-label">Рабочее время</div>
            <div className="work-hours-value">
              <span className="work-hours-start">{schedule.work_start}</span>
              <span className="work-hours-separator">—</span>
              <span className="work-hours-end">{schedule.work_end}</span>
            </div>
            {schedule.late_tolerance_minutes > 0 && (
              <div className="work-hours-tolerance">
                Допустимое опоздание: {schedule.late_tolerance_minutes} мин
              </div>
            )}
          </div>
        )}

        {/* Навигация по месяцам */}
        <div className="schedule-nav">
          <button className="schedule-nav-btn" onClick={prevMonth}>
            ◀
          </button>
          <div className="schedule-nav-title">
            {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
          </div>
          <button className="schedule-nav-btn" onClick={nextMonth}>
            ▶
          </button>
        </div>

        <div className="schedule-content">
          {isLoading ? (
            <div className="schedule-loading">
              <div className="schedule-spinner"></div>
              <p>Загрузка...</p>
            </div>
          ) : error ? (
            <div className="schedule-error">
              <span className="schedule-error-icon">⚠️</span>
              <p>{error}</p>
              <button className="schedule-retry" onClick={loadSchedule}>
                Повторить
              </button>
            </div>
          ) : !schedule ? (
            <div className="schedule-empty">
              <span className="schedule-empty-icon">📭</span>
              <p>График не настроен</p>
              <p className="schedule-empty-hint">Обратитесь к администратору</p>
            </div>
          ) : (
            <>
              {/* Дни недели */}
              <div className="schedule-weekdays">
                {DAY_NAMES.map(day => (
                  <div key={day} className="schedule-weekday">{day}</div>
                ))}
              </div>

              {/* Календарная сетка */}
              <div className="schedule-grid">
                {daysGrid.map((day, index) => {
                  if (!day) {
                    return <div key={`empty-${index}`} className="schedule-day schedule-day--empty"></div>
                  }
                  
                  const dayNum = new Date(day.date).getDate()
                  const classes = [
                    'schedule-day',
                    day.is_work_day ? 'schedule-day--work' : 'schedule-day--off',
                    isToday(day.date) ? 'schedule-day--today' : '',
                    day.exception_type ? `schedule-day--${day.exception_type}` : ''
                  ].filter(Boolean).join(' ')
                  
                  return (
                    <div key={day.date} className={classes} title={day.notes || undefined}>
                      <span className="schedule-day-num">{dayNum}</span>
                      {day.exception_type === 'vacation' && <span className="schedule-day-icon">🏖️</span>}
                      {day.exception_type === 'sick_leave' && <span className="schedule-day-icon">🏥</span>}
                    </div>
                  )
                })}
              </div>

              {/* Легенда */}
              <div className="schedule-legend">
                <div className="legend-item">
                  <div className="legend-color legend-color--work"></div>
                  <span>Рабочий день</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color legend-color--off"></div>
                  <span>Выходной</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color legend-color--vacation"></div>
                  <span>Отпуск</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

